import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  type ChipProps,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { Close, Refresh, Star } from '@mui/icons-material'
import { formatDistanceToNowStrict } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import type { VipUsuarioVigencia } from '../../types'
import { apiService } from '../../services/api'

type VipStatusKey = 'ATIVO' | 'AGENDADO' | 'EXPIRADO' | 'SEM_VIGENCIA'

type VipRow = VipUsuarioVigencia & {
  id: string
  statusKey: VipStatusKey
  vigenciaInicioTs: number | null
  vigenciaFimTs: number | null
}

const dialogBackdropSx = { bgcolor: 'rgba(0,0,0,0.78)' }
const dialogPaperSx = {
  bgcolor: 'rgba(15, 15, 28, 0.96)',
  border: '1px solid rgba(255,255,255,0.10)',
  boxShadow: '0 24px 90px rgba(0,0,0,0.65)'
}

const safeDate = (iso?: string | null) => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d
}

const formatDateTime = (iso?: string | null) => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('pt-BR')
}

const getVipStatusKey = (row: VipUsuarioVigencia, now = Date.now()): VipStatusKey => {
  const inicio = safeDate(row.vigenciaInicio)?.getTime()
  const fim = safeDate(row.vigenciaFim)?.getTime()
  if (!fim) return 'SEM_VIGENCIA'
  if (fim < now) return 'EXPIRADO'
  if (inicio !== undefined && inicio !== null && inicio > now) return 'AGENDADO'
  return 'ATIVO'
}

const statusChipProps = (status: VipStatusKey): { label: string; color: ChipProps['color']; variant?: ChipProps['variant'] } => {
  if (status === 'ATIVO') return { label: 'Ativo', color: 'success' }
  if (status === 'AGENDADO') return { label: 'Agendado', color: 'info' }
  if (status === 'EXPIRADO') return { label: 'Expirado', color: 'error' }
  return { label: '—', color: 'default', variant: 'outlined' }
}

const AdminVipUsuariosPage: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [items, setItems] = useState<VipUsuarioVigencia[]>([])
  const [refreshKey, setRefreshKey] = useState(0)
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null)

  const [term, setTerm] = useState('')
  const [status, setStatus] = useState<VipStatusKey | ''>('')
  const [onlyConcatenated, setOnlyConcatenated] = useState<'0' | '1'>('0')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  const [detailsOpen, setDetailsOpen] = useState(false)
  const [detailsUser, setDetailsUser] = useState<VipUsuarioVigencia | null>(null)

  useEffect(() => {
    let mounted = true

    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await apiService.listarUsuariosVipVigenciaPalworld()
        if (!mounted) return
        setItems(Array.isArray(data) ? data : [])
        setLastUpdatedAt(new Date())
      } catch (e: any) {
        if (!mounted) return
        setError(e?.message || 'Erro ao consultar usuários VIP.')
      } finally {
        if (!mounted) return
        setLoading(false)
      }
    }

    run()
    return () => {
      mounted = false
    }
  }, [refreshKey])

  useEffect(() => {
    setPage(0)
  }, [onlyConcatenated, status, term])

  const filtered = useMemo(() => {
    const t = term.trim().toLowerCase()
    const now = Date.now()

    return items.filter((r) => {
      if (onlyConcatenated === '1' && !r.periodoConcatenado) return false
      if (status) {
        const s = getVipStatusKey(r, now)
        if (s !== status) return false
      }
      if (!t) return true

      const blob = [
        r.email,
        r.playerName
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      return blob.includes(t)
    })
  }, [items, onlyConcatenated, status, term])

  const rows: VipRow[] = useMemo(() => {
    const now = Date.now()
    return filtered.map((r, idx) => ({
      ...r,
      id: r.userId || `${r.email ?? 'row'}-${idx}`,
      statusKey: getVipStatusKey(r, now),
      vigenciaInicioTs: safeDate(r.vigenciaInicio)?.getTime() ?? null,
      vigenciaFimTs: safeDate(r.vigenciaFim)?.getTime() ?? null
    }))
  }, [filtered])

  const stats = useMemo(() => {
    const now = Date.now()
    let ativos = 0
    let agendados = 0
    let expirados = 0
    let semVigencia = 0
    let concatenados = 0

    for (const r of items) {
      if (r.periodoConcatenado) concatenados += 1
      const s = getVipStatusKey(r, now)
      if (s === 'ATIVO') ativos += 1
      else if (s === 'AGENDADO') agendados += 1
      else if (s === 'EXPIRADO') expirados += 1
      else semVigencia += 1
    }

    return { total: items.length, ativos, agendados, expirados, semVigencia, concatenados }
  }, [items])

  const totalFiltered = rows.length

  const rowsPage = useMemo(() => {
    const start = page * rowsPerPage
    return rows.slice(start, start + rowsPerPage)
  }, [page, rows, rowsPerPage])

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(totalFiltered / rowsPerPage) - 1)
    if (page > maxPage) setPage(0)
  }, [page, rowsPerPage, totalFiltered])

  const openDetails = useCallback((u: VipUsuarioVigencia) => {
    setDetailsUser(u)
    setDetailsOpen(true)
  }, [])

  const closeDetails = useCallback(() => {
    setDetailsOpen(false)
  }, [])

  const onRefresh = useCallback(() => setRefreshKey((v) => v + 1), [])

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={2.5}>
        <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 280 }}>
            <Typography variant="h4" sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Star />
              Usuários VIP
            </Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              Acompanhe e gerencie a vigência do VIP.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            {lastUpdatedAt && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Atualizado em {lastUpdatedAt.toLocaleString('pt-BR')}
              </Typography>
            )}
            <Tooltip title="Atualizar lista">
              <span>
                <Button variant="outlined" startIcon={<Refresh />} onClick={onRefresh} disabled={loading}>
                  Atualizar
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2.5,
                      display: 'grid',
                      placeItems: 'center',
                      border: '1px solid rgba(255,255,255,0.12)',
                      bgcolor: 'rgba(255,255,255,0.06)'
                    }}
                  >
                    <Star sx={{ opacity: 0.8 }} />
                  </Box>
                  <Box>
                    <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1 }}>
                      Total
                    </Typography>
                    <Typography sx={{ fontWeight: 950, fontSize: 28, lineHeight: 1.1 }}>
                      {stats.total.toLocaleString('pt-BR')}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2.5,
                      display: 'grid',
                      placeItems: 'center',
                      border: '1px solid rgba(0,230,118,0.30)',
                      bgcolor: 'rgba(0,230,118,0.10)'
                    }}
                  >
                    <Star sx={{ color: 'success.main' }} />
                  </Box>
                  <Box>
                    <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1 }}>
                      Ativos
                    </Typography>
                    <Typography sx={{ fontWeight: 950, fontSize: 28, lineHeight: 1.1, color: 'success.main' }}>
                      {stats.ativos.toLocaleString('pt-BR')}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2.5,
                      display: 'grid',
                      placeItems: 'center',
                      border: '1px solid rgba(244,67,54,0.30)',
                      bgcolor: 'rgba(244,67,54,0.10)'
                    }}
                  >
                    <Star sx={{ color: 'error.main' }} />
                  </Box>
                  <Box>
                    <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1 }}>
                      Expirados
                    </Typography>
                    <Typography sx={{ fontWeight: 950, fontSize: 28, lineHeight: 1.1, color: 'error.main' }}>
                      {stats.expirados.toLocaleString('pt-BR')}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2.5,
                      display: 'grid',
                      placeItems: 'center',
                      border: '1px solid rgba(0,217,255,0.30)',
                      bgcolor: 'rgba(0,217,255,0.10)'
                    }}
                  >
                    <Star sx={{ color: 'info.main' }} />
                  </Box>
                  <Box>
                    <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1 }}>
                      Concatenados
                    </Typography>
                    <Typography sx={{ fontWeight: 950, fontSize: 28, lineHeight: 1.1, color: 'info.main' }}>
                      {stats.concatenados.toLocaleString('pt-BR')}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card>
          <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Buscar"
                  placeholder="Email ou jogador..."
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value as any)}>
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="ATIVO">Ativo</MenuItem>
                    <MenuItem value="AGENDADO">Agendado</MenuItem>
                    <MenuItem value="EXPIRADO">Expirado</MenuItem>
                    <MenuItem value="SEM_VIGENCIA">Sem vigência</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Concatenado</InputLabel>
                  <Select
                    value={onlyConcatenated}
                    label="Concatenado"
                    onChange={(e) => setOnlyConcatenated(e.target.value as any)}
                  >
                    <MenuItem value="0">Todos</MenuItem>
                    <MenuItem value="1">Somente concatenados</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' } }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Exibindo <strong>{totalFiltered.toLocaleString('pt-BR')}</strong> de{' '}
                    <strong>{items.length.toLocaleString('pt-BR')}</strong> usuários.
                  </Typography>
                  <Divider flexItem orientation="vertical" sx={{ display: { xs: 'none', sm: 'block' } }} />
                  <Button variant="text" onClick={() => setTerm('')} disabled={!term}>
                    Limpar busca
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => {
                      setStatus('')
                      setOnlyConcatenated('0')
                    }}
                    disabled={!status && onlyConcatenated === '0'}
                  >
                    Limpar filtros
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {error && <Alert severity="error">{error}</Alert>}

        <Card>
          <CardContent sx={{ p: 0 }}>
            {loading ? (
              <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
                <Stack spacing={1} alignItems="center">
                  <CircularProgress />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Carregando usuários VIP...
                  </Typography>
                </Stack>
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: 'rgba(255,255,255,0.03)' }}>
                  <Table
                    size="small"
                    stickyHeader
                    sx={{
                      '& .MuiTableCell-head': {
                        bgcolor: 'rgba(7, 8, 18, 0.92)',
                        backdropFilter: 'blur(10px)',
                        borderBottom: '1px solid rgba(255,255,255,0.08)'
                      }
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 950 }}>Jogador</TableCell>
                        <TableCell sx={{ fontWeight: 950 }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 950 }}>Vigência</TableCell>
                        <TableCell sx={{ fontWeight: 950 }} align="center">
                          Períodos
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rowsPage.map((r) => {
                        const props = statusChipProps(r.statusKey)
                        const now = Date.now()

                        const inicio = r.vigenciaInicioTs
                        const fim = r.vigenciaFimTs
                        const hasWindow = !!inicio && !!fim && fim > inicio

                        const relative =
                          r.statusKey === 'ATIVO' && fim
                            ? `Expira ${formatDistanceToNowStrict(new Date(fim), { addSuffix: true, locale: ptBR })}`
                            : r.statusKey === 'AGENDADO' && inicio
                              ? `Começa ${formatDistanceToNowStrict(new Date(inicio), { addSuffix: true, locale: ptBR })}`
                              : r.statusKey === 'EXPIRADO' && fim
                                ? `Terminou ${formatDistanceToNowStrict(new Date(fim), { addSuffix: true, locale: ptBR })}`
                                : null

                        const progress =
                          r.statusKey === 'ATIVO' && hasWindow ? Math.min(100, Math.max(0, ((now - inicio!) / (fim! - inicio!)) * 100)) : r.statusKey === 'EXPIRADO' ? 100 : 0

                        const periodsCount =
                          Number(r.quantidadeVipsConcatenados ?? (r.vipsConcatenados?.length ?? 0) ?? 0) || 0

                        return (
                          <TableRow
                            key={r.id}
                            hover
                            sx={{
                              '&:hover td': { bgcolor: 'rgba(0,217,255,0.04)' },
                              '& td': { borderBottomColor: 'rgba(255,255,255,0.06)' }
                            }}
                          >
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="body2" sx={{ fontWeight: 950, lineHeight: 1.2 }}>
                                {r.playerName ?? '—'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                VIP: {r.statusKey.toLowerCase()}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                {r.email ?? '—'}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ py: 2, minWidth: 340 }}>
                              <Stack spacing={0.75}>
                                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                                  <Chip
                                    size="small"
                                    {...props}
                                    sx={{
                                      fontWeight: 950,
                                      borderColor:
                                        props.color === 'success'
                                          ? alpha('#00e676', 0.35)
                                          : props.color === 'info'
                                            ? alpha('#00d9ff', 0.35)
                                            : props.color === 'error'
                                              ? alpha('#f44336', 0.35)
                                              : 'rgba(255,255,255,0.16)',
                                      bgcolor:
                                        props.color === 'success'
                                          ? alpha('#00e676', 0.12)
                                          : props.color === 'info'
                                            ? alpha('#00d9ff', 0.10)
                                            : props.color === 'error'
                                              ? alpha('#f44336', 0.10)
                                              : 'transparent'
                                    }}
                                  />
                                  {r.periodoConcatenado && (
                                    <Chip
                                      size="small"
                                      label={`${periodsCount.toLocaleString('pt-BR')} períodos`}
                                      variant="outlined"
                                      sx={{ fontWeight: 900, borderColor: 'rgba(255,255,255,0.18)' }}
                                    />
                                  )}
                                </Stack>

                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  {formatDateTime(r.vigenciaInicio)} → {formatDateTime(r.vigenciaFim)}
                                </Typography>

                                {relative && (
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {relative}
                                  </Typography>
                                )}

                                {hasWindow && (
                                  <Box
                                    sx={{
                                      height: 8,
                                      borderRadius: 99,
                                      overflow: 'hidden',
                                      border: '1px solid rgba(255,255,255,0.10)',
                                      bgcolor: 'rgba(255,255,255,0.04)'
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        height: '100%',
                                        width: `${progress}%`,
                                        background:
                                          r.statusKey === 'EXPIRADO'
                                            ? 'linear-gradient(90deg, rgba(244,67,54,0.55), rgba(244,67,54,0.25))'
                                            : r.statusKey === 'AGENDADO'
                                              ? 'linear-gradient(90deg, rgba(0,217,255,0.45), rgba(0,217,255,0.18))'
                                              : 'linear-gradient(90deg, rgba(0,230,118,0.60), rgba(0,217,255,0.22))'
                                      }}
                                    />
                                  </Box>
                                )}
                              </Stack>
                            </TableCell>

                            <TableCell align="center" sx={{ py: 2 }}>
                              <Tooltip title={periodsCount ? 'Ver períodos VIP' : 'Sem períodos concatenados'}>
                                <span>
                                  <Button size="small" variant="outlined" disabled={!periodsCount} onClick={() => openDetails(r)}>
                                    Ver
                                  </Button>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        )
                      })}

                      {rowsPage.length === 0 && !error && (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ py: 6 }}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Star sx={{ fontSize: 64, color: 'rgba(255,255,255,0.12)' }} />
                              <Typography variant="h6" sx={{ mt: 1, fontWeight: 950 }}>
                                Nenhum usuário encontrado
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Ajuste os filtros ou atualize a lista.
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={totalFiltered}
                  page={page}
                  onPageChange={(_e, p) => setPage(p)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10))
                    setPage(0)
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  labelRowsPerPage="Linhas por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`}
                  sx={{
                    borderTop: '1px solid rgba(255,255,255,0.08)',
                    '& .MuiTablePagination-toolbar': { px: 2 }
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>

        <Dialog
          open={detailsOpen}
          onClose={closeDetails}
          fullWidth
          maxWidth="md"
          BackdropProps={{ sx: dialogBackdropSx }}
          PaperProps={{ sx: dialogPaperSx }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                Períodos VIP
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {detailsUser?.playerName ?? '—'} • {detailsUser?.email ?? '—'}
              </Typography>
            </Box>
            <Button variant="text" startIcon={<Close />} onClick={closeDetails}>
              Fechar
            </Button>
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <Card>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Vigência
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {formatDateTime(detailsUser?.vigenciaInicio)} → {formatDateTime(detailsUser?.vigenciaFim)}
                  </Typography>
                </CardContent>
              </Card>

              <Card>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ px: 2.5, py: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                      Períodos concatenados
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {Number(detailsUser?.quantidadeVipsConcatenados ?? (detailsUser?.vipsConcatenados?.length ?? 0) ?? 0) || 0}{' '}
                      período(s).
                    </Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ px: 2.5, py: 2 }}>
                    {(detailsUser?.vipsConcatenados ?? []).length === 0 ? (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Nenhum período disponível.
                      </Typography>
                    ) : (
                      <Stack spacing={1.2}>
                        {(detailsUser?.vipsConcatenados ?? []).map((p, idx) => {
                          const isActive = !!detailsUser?.vipAtivoId && p.vipId === detailsUser.vipAtivoId
                          const fimDate = safeDate(p.fim)
                          return (
                            <Box
                              key={p.vipId}
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                border: '1px solid rgba(255,255,255,0.10)',
                                bgcolor: 'rgba(255,255,255,0.03)',
                                display: 'flex',
                                gap: 2,
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap'
                              }}
                            >
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                  Período {idx + 1}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  {formatDateTime(p.inicio)} → {formatDateTime(p.fim)}
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                {isActive && <Chip size="small" label="Ativo" color="success" />}
                                {fimDate ? (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`Fim ${formatDistanceToNowStrict(fimDate, { addSuffix: true, locale: ptBR })}`}
                                  />
                                ) : (
                                  <Chip size="small" variant="outlined" label="Fim —" />
                                )}
                              </Stack>
                            </Box>
                          )
                        })}
                      </Stack>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={closeDetails} variant="contained">
              Ok
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Container>
  )
}

export default AdminVipUsuariosPage
